import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationDetailsDto,
    Lotam,
    MissionNoticeAcknowledgementDto,
    MissionNoticeDto,
    NoticesService,
    Organisation,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { TableColumn, TableConfig } from '@flyfreely-portal-ui/flyfreely-table';
import { FormatDateTimePipe } from '@flyfreely-portal-ui/ui';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject, takeUntil } from 'rxjs';
import { MissionNoticeAcknowledger } from '../mission-notice-acknowledgers';

interface AcknowledgementRow {
    personName: string;
    /** Raw ISO acknowledgement time, used for chronological sorting. */
    acknowledgementTime: string;
    /** Formatted acknowledgement time, used for display. */
    acknowledgementDate: string;
}

@Component({
    selector: 'mission-lotam-details',
    templateUrl: './mission-lotam-details.component.html',
    styleUrls: ['./mission-lotam-details.component.scss']
})
export class MissionLotamDetailsComponent implements OnInit {
    @Input() lotam: Lotam;
    @Input() organisation: Organisation;
    @Input() missionId: number;
    @Input() missionLocation: LocationDetailsDto;
    @Input() isReadOnly = false;
    @Input() isAcknowledgedInMission = false;
    @Input() acknowledgers: MissionNoticeAcknowledger[] = [];
    @Input() status: MissionNoticeDto.Status;
    @Output() acknowledge = new EventEmitter<MissionNoticeAcknowledgementDto>();

    acknowledgementRows: AcknowledgementRow[] = [];
    acknowledgementColumns: TableColumn<AcknowledgementRow>[] = [
        {
            name: 'Name',
            value: 'personName',
            searchable: false,
            selectable: false,
            defaultSelection: true,
            sortable: true
        },
        {
            name: 'Acknowledged',
            key: 'acknowledgementTime',
            value: 'acknowledgementDate',
            searchable: false,
            selectable: false,
            defaultSelection: true,
            sortable: true,
            compareFunction: (a, b) =>
                (a.rawData ?? '').localeCompare(b.rawData ?? '')
        }
    ];
    acknowledgementTableConfig: TableConfig<AcknowledgementRow>;

    private workTracker = new WorkTracker();
    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private modal: BsModalRef<MissionLotamDetailsComponent>,
        private noticesService: NoticesService,
        private logging: FlyFreelyLoggingService,
        private formatDateTime: FormatDateTimePipe
    ) {}

    ngOnInit() {
        this.acknowledgementTableConfig = {
            limit: null,
            actions: [],
            timeZone: this.organisation?.timeZone
        };
        this.acknowledgementRows = (this.acknowledgers ?? []).map(a => ({
            personName: a.name ?? 'Not on the mission crew',
            acknowledgementTime: a.acknowledgementTime,
            acknowledgementDate:
                this.formatDateTime.transform(
                    a.acknowledgementTime,
                    this.organisation?.timeZone
                ) ?? ''
        }));
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    onCancel() {
        this.modal.hide();
    }

    onAcknowledgeForMission() {
        this.noticesService
            .acknowledgeMissionNotice(this.missionId, this.lotam.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    this.isAcknowledgedInMission = true;
                    this.acknowledge.emit(result);
                    this.logging.success(
                        `Successfully acknowledge LOTAM ${this.lotam.subject}`
                    );
                    this.modal.hide();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while acknowledge LOTAM: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }
}
